import { BodyText } from '@/utils/global-fonts';
import { getNestedErrorMessage } from '@/utils/utils';
import PropTypes from 'prop-types';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ValidationErrorMessage from '@/components/error/validation-error-component';
import Styled from './toggle-switch-style';

/**
 * Register options for React Hook Form.
 *
 * @typedef {object} RegisterOptions
 * @property {React.Ref<any>} [ref] - React element ref.
 * @property {boolean | { value: boolean, message: string }} [required] - A Boolean which, if true, indicates that the input must have a value before the form can be submitted. You can assign a string to return an error message in the errors object. Note: This config aligns with web constrained API for required input validation, for object or array type of input use validate function instead.
 * @property {{ value: number, message: string }} [maxLength] - The maximum length of the input value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [minLength] - The minimum length of the input value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [max] - The maximum allowed numeric value, and an optional `message` (string) for the error message.
 * @property {{ value: number, message: string }} [min] - The minimum allowed numeric value, and an optional `message` (string) for the error message.
 * @property {{ value: RegExp, message: string }} [pattern] - A regular expression to match the input value, and an optional `message` (string) for the error message.
 * @property {(value: any) => boolean | string | Promise<boolean | string>} [validate] - A custom validation function or a function returning a promise resolving to a boolean or string. If a string is returned, it will be used as the error message.
 * @property {boolean} [valueAsNumber] - Whether to treat the value as a number (useful for numeric inputs).
 * @property {boolean} [valueAsDate] - Whether to treat the value as a date.
 * @property {(value: any) => any} [setValueAs] - A function to transform the value before setting it in the form state.
 * @property {boolean} [disabled] - Whether the input is disabled (default: false).
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} [onChange] - A function to handle input changes.
 * @property {(event: React.FocusEvent<HTMLInputElement>) => void} [onBlur] - A function to handle input blur events.
 * @property {any} [value] - The initial value of the input.
 * @property {boolean} [shouldUnregister] - Whether to unregister the input on unmount.
 * @property {string | string[]} [deps] - The names of other fields that affect validation of this input.
 */

/**
 * This is a Toggle component that can be used throughout ETTX.
 * @param {object} props - The props of the component.
 * @param {string} props.name - The name of the input for React Hook Form (required).
 * @param {boolean} props.disabled - The disabled state of the toggle.
 * @param {bool} props.checked - The checked value of the component.
 * @param {func} props.onChange - The change handler of the component
 * @param {string} props.label - The text element of the component.
 * @param {string} props.margin - The margin values of the component.
 * @param {string} props.cy - The name of the input for Cypress tests.
 * @param {object} props.style - The style object for the component.
 * @param {RegisterOptions} props.registerOptions - The register options for React Hook Form.
 * @returns {JSX.Element} The Toggle component.
 */
const ToggleSwitch = ({
  disabled,
  registerOptions,
  checked,
  name,
  onChange,
  label,
  margin,
  cy,
  style,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const errorMessage = getNestedErrorMessage(name, errors);

  return (
    <div style={style}>
      <Styled.Row>
        <Styled.InputContainer $margin={margin}>
          <Controller
            control={control}
            name={name}
            defaultValue={checked}
            rules={registerOptions}
            render={({ field }) => (
              <Styled.InputContainer>
                <Styled.Input
                  data-cy={cy}
                  type="checkbox"
                  name={name}
                  checked={field.value}
                  {...field}
                  onChange={() => {
                    field.onChange(!field.value);
                    if (onChange) onChange(!field.value);
                  }}
                  disabled={disabled}
                />
                <Styled.Slider
                  checked={field.value}
                  disabled={disabled}
                  toggled={field.value ? null : 'toggled'}
                />
              </Styled.InputContainer>
            )}
          />
        </Styled.InputContainer>
        <BodyText>{label}</BodyText>
      </Styled.Row>
      <ValidationErrorMessage message={errorMessage} />
    </div>
  );
};

ToggleSwitch.defaultProps = {
  disabled: false,
  checked: false,
  label: '',
  registerOptions: {
    onChange: () => {},
  },
  margin: '0px',
  cy: undefined,
  onChange: undefined,
  style: {},
};

ToggleSwitch.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  label: PropTypes.string,
  registerOptions: PropTypes.shape({}),
  margin: PropTypes.string,
  cy: PropTypes.string,
  style: PropTypes.shape({}),
};

export default ToggleSwitch;
